import { useEffect, useState } from "react";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { SignaturePad } from "./SignaturePad";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const SECTIONS = [
  ["general", "General Area of Home", [
    ["traffic_clear", "Traffic areas free of clutter & furniture"], ["no_loose_rugs", "No loose carpeting / area rugs"],
    ["wires_tucked", "Electrical wires tucked away"], ["bright_lighting", "Bright lighting in rooms, floors & stairs"],
    ["switches_work", "Light switches work"], ["bulbs_working", "Light bulbs all working"],
    ["furniture_stable", "Furniture is stable"], ["chairs_easy", "Easy to get in and out of chairs"],
    ["flooring_safe", "Flooring free of turned-up edges/cracks"], ["water_temp_120", "Hot water tank set to 120°"],
    ["front_door_access", "Front door to street access"], ["backup_generator", "Back-up generator"],
  ]],
  ["bathrooms", "Bathrooms", [
    ["care_items_reach", "Personal care items within reach"], ["taps_marked", "Hot/cold taps marked clearly"],
    ["bath_brightly_lit", "Room brightly lit"], ["nonslip_mats", "Non-slip mats in bath/shower"],
    ["toilet_height", "Toilet seat correct height"], ["grab_bars", "Grab bars in shower/bath/near toilet"],
    ["bath_easy_access", "Easy access to bath/shower"],
  ]],
  ["outdoor", "Outdoor Maintenance", [
    ["grounds_maintained", "Grounds maintained (all seasons)"], ["outdoor_handrails", "Handrails installed"],
    ["garbage_out", "Garbage & recycling get out each week"],
  ]],
  ["alarms", "Alarms & Security", [
    ["smoke_co_detectors", "Smoke & CO detectors work"], ["fall_alert", "Fall alert system in place"],
    ["call_bell", "Call caregiver bell system"], ["home_security", "Home security / central alarm / video"],
    ["emergency_exit_plan", "Emergency exit plan"], ["digital_doorbell", "Digital doorbell"],
    ["outdoor_lights", "Outdoor lights on timer/motion"], ["key_hidden", "Key hidden outdoors somewhere"],
  ]],
  ["bedrooms", "Bedrooms", [
    ["bedroom_bright", "Rooms brightly lit"], ["bedroom_clutter_free", "Area free of clutter"],
    ["rugs_secure", "Rugs secure to floor"], ["bed_height", "Bed right height for safe transfers"],
    ["phone_access", "Access to phone"], ["bedside_lamp", "Bedside table and lamp"],
    ["path_bathroom", "Clear path to bathroom"], ["dressing_chair", "Chair for dressing"],
  ]],
  ["kitchen", "Kitchen", [
    ["items_reachable", "Items placed within reach"], ["oven_works", "Oven & burners work"],
    ["microwave_works", "Microwave works"], ["kitchen_light", "Adequate light for cooking"],
    ["food_rotated", "Food is being rotated"], ["sit_food_prep", "Area to sit for food prep"],
    ["extra_food", "Extra non-perishable food supply"],
  ]],
  ["garage", "Garage (if applicable)", [
    ["garage_organized", "Organized"], ["garage_easy_move", "Easy to move around"],
    ["garage_accessible", "Items accessible (not piled up)"], ["garage_bright", "Brightly lit"],
    ["garage_door_works", "Garage door works"], ["garage_door_secure", "Garage door is secure"],
  ]],
  ["stairs", "Stairs", [
    ["stairs_free", "Free of items"], ["stairs_handrails", "Handrails both sides secure"], ["stairs_no_edges", "No carpet edges to trip on"],
  ]],
];

const LIVING = ["Private home", "Apt", "Condo", "Room in care facility", "Married", "Lives with spouse/family/friend", "Lives alone"];

export const HomeSafetyCard = ({ clientId, onChanged }) => {
  const [hs, setHs] = useState(null);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = () => api.get(`/clients/${clientId}/home-safety`).then((r) => setHs(r.data.home_safety)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  if (!hs) return null;
  const signed = hs.signed?.signed_at;
  const itemStatus = (k) => (hs.items || {})[k] || "";
  const totalItems = SECTIONS.reduce((n, [, , items]) => n + items.length, 0);
  const assessedCount = SECTIONS.reduce((n, [, , items]) => n + items.filter(([k]) => itemStatus(k)).length, 0);
  // the evaluation's job is to uncover dangers — only items the evaluator marks as a danger count
  const dangers = SECTIONS.flatMap(([, title, items]) =>
    items.filter(([k]) => itemStatus(k) === "danger").map(([k, label]) => ({ k, label, section: title })));

  const set = (k) => (e) => setHs({ ...hs, [k]: e.target.value });
  const setStatus = (k, v) => {
    const items = { ...(hs.items || {}), [k]: itemStatus(k) === v ? "" : v };
    const issue_notes = { ...(hs.issue_notes || {}) };
    if (items[k] !== "danger") delete issue_notes[k];
    setHs({ ...hs, items, issue_notes });
  };
  const setIssueNote = (k, v) => setHs({ ...hs, issue_notes: { ...(hs.issue_notes || {}), [k]: v } });
  const toggleLiving = (o) => {
    const cur = new Set(hs.living || []);
    if (cur.has(o)) cur.delete(o); else cur.add(o);
    setHs({ ...hs, living: Array.from(cur) });
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/clients/${clientId}/home-safety`, hs);
      setHs(data.home_safety);
      toast.success("Home safety evaluation saved");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sign = async () => {
    if (!sig) { toast.error("Draw the evaluator signature first"); return; }
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/home-safety/sign`, { signer_name: hs.evaluator_name, signature_png: sig });
      toast.success("Evaluation signed and filed");
      setSig(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const preview = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/home-safety/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="home-safety-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-heading text-sm font-semibold text-stone-900">Home Environment Safety Evaluation</p>
          <p className="mt-0.5 text-xs text-stone-500">
            Walk the home and mark each item Safe, Danger, or N/A — the report leads with the dangers you find.
            {assessedCount > 0 && (
              <span data-testid="hs-issue-count">
                {" "}{assessedCount}/{totalItems} assessed —{" "}
                <span className={dangers.length ? "font-semibold text-[#C0392B]" : "font-semibold text-[#1e8449]"}>
                  {dangers.length} danger{dangers.length === 1 ? "" : "s"} identified
                </span>
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {signed ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-3 py-1 text-xs font-medium text-[#1e8449]" data-testid="home-safety-signed-badge">
              <CheckCircle size={14} weight="fill" /> Signed by {hs.signed.signer_name} {String(signed).slice(0, 10)}
            </span>
          ) : (
            <span className="rounded-full border border-stone-200 px-3 py-1 text-xs text-stone-400" data-testid="home-safety-unsigned-badge">Not signed</span>
          )}
          <button onClick={() => setExpanded(!expanded)} className="text-xs font-medium text-primary hover:underline" data-testid="home-safety-toggle">
            {expanded ? "Collapse" : "Open evaluation"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input value={hs.address || ""} onChange={set("address")} className="border-stone-300 bg-white" data-testid="hs-address" />
            </div>
            <div className="space-y-1.5">
              <Label>Contact info</Label>
              <Input value={hs.contact || ""} onChange={set("contact")} className="border-stone-300 bg-white" data-testid="hs-contact" />
            </div>
          </div>

          {SECTIONS.map(([key, title, items]) => (
            <div key={key} data-testid={`hs-section-${key}`}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-stone-500">{title}</p>
              <div className="space-y-1.5">
                {items.map(([k, label]) => (
                  <div key={k} data-testid={`hs-row-${k}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="min-w-52 flex-1 text-xs text-stone-600">{label}</span>
                      <div className="flex gap-1">
                        {[["safe", "Safe"], ["danger", "Danger"], ["na", "N/A"]].map(([v, lbl]) => (
                          <button key={v} type="button" onClick={() => setStatus(k, v)} data-testid={`hs-item-${k}-${v}`}
                            className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                              itemStatus(k) === v
                                ? v === "danger" ? "border-[#C0392B]/40 bg-[#C0392B]/15 text-[#C0392B]"
                                : v === "safe" ? "border-[#27AE60]/40 bg-[#27AE60]/15 text-[#1e8449]"
                                : "border-stone-300 bg-stone-100 text-stone-600"
                                : "border-stone-200 bg-white text-stone-400 hover:border-stone-400 hover:text-stone-600"
                            }`}>{lbl}</button>
                        ))}
                      </div>
                    </div>
                    {itemStatus(k) === "danger" && (
                      <Input placeholder="What's the danger / what needs to happen?" value={(hs.issue_notes || {})[k] || ""}
                        onChange={(e) => setIssueNote(k, e.target.value)}
                        className="mt-1 h-8 border-[#C0392B]/30 bg-white text-xs" data-testid={`hs-issue-note-${k}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {dangers.length > 0 && (
            <div className="rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/[0.04] p-4" data-testid="hs-issues-panel">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#C0392B]">
                Dangers identified ({dangers.length})
              </p>
              <ul className="mt-2 space-y-1">
                {dangers.map((it) => (
                  <li key={it.k} className="text-xs text-stone-700" data-testid={`hs-issue-${it.k}`}>
                    <span className="font-medium">{it.label}</span> <span className="text-stone-400">· {it.section}</span>
                    {(hs.issue_notes || {})[it.k] ? <span> — {(hs.issue_notes || {})[it.k]}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-stone-500">Current living arrangement</p><div className="flex flex-wrap gap-3" data-testid="hs-living">
              {LIVING.map((o) => (
                <label key={o} className="flex items-center gap-1.5 text-xs text-stone-600">
                  <input type="checkbox" checked={(hs.living || []).includes(o)} onChange={() => toggleLiving(o)} />
                  {o}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Input value={hs.notes || ""} onChange={set("notes")} className="border-stone-300 bg-white" data-testid="hs-notes" />
          </div>

          <div className="grid grid-cols-1 gap-4 border-t border-stone-100 pt-4 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Inspection completed by</Label>
              <Input value={hs.evaluator_name || ""} onChange={set("evaluator_name")} className="border-stone-300 bg-white" data-testid="hs-evaluator" />
            </div>
            <div className="space-y-1.5">
              <Label>Title / position</Label>
              <Input value={hs.evaluator_title || ""} onChange={set("evaluator_title")} className="border-stone-300 bg-white" data-testid="hs-evaluator-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={hs.evaluated_at || ""} onChange={set("evaluated_at")} className="border-stone-300 bg-white" data-testid="hs-date" />
            </div>
          </div>

          {!signed && (
            <div className="space-y-3">
              <Label>Evaluator signature</Label>
              <SignaturePad onChange={setSig} />
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={preview} className="border-stone-300" data-testid="hs-preview">Preview PDF</Button>
            <Button variant="outline" onClick={save} disabled={busy} className="border-stone-300" data-testid="hs-save">
              {busy ? "Saving…" : "Save"}
            </Button>
            {!signed && (
              <Button onClick={sign} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="hs-sign">
                {busy ? "Signing…" : "Sign & file"}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
